import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest, of, zip, throwError } from 'rxjs';
import { catchError, map, switchMap, refCount, publishReplay, skipWhile, filter } from 'rxjs/operators';
import { Currency, Package, Price, Pricing, Frequency, PackageService } from '@vendasta/marketplace-packages';
import {
  Order,
  RevenuePeriod,
  SalesOrdersService,
  ConfigInterface,
  LineItem,
  LineItemInterface,
} from '@vendasta/sales-orders';
import { App, AppKey, AppPartnerService } from '@galaxy/marketplace-apps';
import { AccountGroup, AccountGroupService, ProjectionFilter, ReadFilter } from '@galaxy/account-group';
import { Salesperson, SalespersonService } from '@vendasta/sales';
import { SalesOrderStoreService } from '../sales-order-store.service';

@Injectable()
export class SalesOrderService {
  private businessId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly businessId$: Observable<string> = this.businessId$$.asObservable();

  private orderId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly orderId$: Observable<string> = this.orderId$$.asObservable();

  private specifiedLineItems$$: BehaviorSubject<LineItemInterface[]> = new BehaviorSubject<LineItemInterface[]>([]);
  readonly specifiedLineItems$: Observable<LineItemInterface[]> = this.specifiedLineItems$$.asObservable();

  readonly lineItems$: Observable<LineItemInterface[]>;

  readonly order$: Observable<Order>;
  readonly business$: Observable<AccountGroup>;
  readonly salesperson$: Observable<Salesperson>;
  readonly packageIds$: Observable<string[]>;
  readonly packages$: Observable<Package[]>;
  readonly apps$: Observable<App[]>;
  readonly standaloneAppKeys$: Observable<AppKey[]>;
  readonly includedAppKeys$: Observable<AppKey[]>;
  readonly orderConfig$: Observable<ConfigInterface>;
  readonly uniqueApps$: Observable<App[]>;

  static convertOrderCurrencyToMPCurrency(orderCurrency: string): Currency {
    if (!Currency[orderCurrency]) {
      return Currency.USD;
    }
    return Currency[orderCurrency];
  }

  static convertOrderRevenuePeriodToMPFrequency(revenuePeriod: RevenuePeriod): Frequency {
    // TODO: Unify Frequency between orders and packages
    switch (revenuePeriod) {
      case undefined: // proto doesn't transmit 0 enum, but the default should not be Onetime
      case RevenuePeriod.ONETIME:
        return Frequency.ONCE;
      case RevenuePeriod.DAILY:
        return Frequency.DAILY;
      case RevenuePeriod.WEEKLY:
        return Frequency.WEEKLY;
      case RevenuePeriod.MONTHLY:
        return Frequency.MONTHLY;
      case RevenuePeriod.YEARLY:
        return Frequency.YEARLY;
      case RevenuePeriod.BIWEEKLY: // No MP equivalent...?
      default:
        return Frequency.OTHER;
    }
  }

  static getPricingFromLineItem(lineItem: LineItem): Pricing {
    let prices: Price[] = [];
    if (!!lineItem.currentRevenue && !!lineItem?.currentRevenue?.revenueComponents) {
      prices = lineItem?.currentRevenue?.revenueComponents.map((c) => {
        return new Price({
          price: c.value,
          frequency: SalesOrderService.convertOrderRevenuePeriodToMPFrequency(c.period),
        });
      });
    }

    return new Pricing({
      currency: SalesOrderService.convertOrderCurrencyToMPCurrency(lineItem.currencyCode),
      prices: prices,
    });
  }

  constructor(
    private accountGroupService: AccountGroupService,
    private salesService: SalespersonService,
    private packageService: PackageService,
    private appsService: AppPartnerService,
    private salesOrdersService: SalesOrdersService,
    private salesOrderStoreService: SalesOrderStoreService,
  ) {
    this.order$ = combineLatest([this.orderId$, this.businessId$]).pipe(
      skipWhile(([orderId, businessId]) => orderId === null || businessId === null),
      switchMap(([orderId, businessId]) => this.salesOrderStoreService.getOrderState(orderId, businessId)),
      filter((orderState) => !orderState.loading),
      map((orderState) => orderState.order),
      publishReplay(1),
      refCount(),
    );

    this.business$ = this.businessId$.pipe(
      switchMap((businessId) => {
        return this.accountGroupService.getMulti(
          [businessId],
          new ProjectionFilter({
            accountGroupExternalIdentifiers: true,
            napData: true,
            accounts: true,
          }),
          new ReadFilter({ includeDeleted: true }),
        );
      }),
      map((results) => {
        if (results.length !== 1) {
          throwError(new Error('Business not found'));
        }
        return results[0];
      }),
      publishReplay(1),
      refCount(),
    );

    this.salesperson$ = this.business$.pipe(
      switchMap((business) => {
        if (!business.externalIdentifiers.salesPersonId) {
          return of(null);
        }
        return this.salesService.getSalesperson(
          business.externalIdentifiers.partnerId,
          business.externalIdentifiers.salesPersonId,
        );
      }),
      catchError(() => {
        // This is only used to pre-fill salesperson a new order, so just skip it if an error occurs
        return of(null);
      }),
      publishReplay(1),
      refCount(),
    );

    this.orderConfig$ = this.business$.pipe(
      switchMap((business) => {
        return this.salesOrdersService.getConfig(
          business.externalIdentifiers.partnerId,
          business.externalIdentifiers.marketId,
        );
      }),
      publishReplay(1),
      refCount(),
    );

    this.lineItems$ = combineLatest([this.order$, this.specifiedLineItems$]).pipe(
      map(([order, lineItems]) => lineItems.concat(order?.lineItems || [])),
      publishReplay(1),
      refCount(),
    );

    this.packageIds$ = this.lineItems$.pipe(
      map((lineItems) => {
        return lineItems.map((lineItem) => lineItem.packageId).filter((packageId) => !!packageId);
      }),
      publishReplay(1),
      refCount(),
    );

    this.standaloneAppKeys$ = this.lineItems$.pipe(
      map((lineItems) => {
        return lineItems
          .filter((lineItem) => !!lineItem.appKey)
          .map((lineItem) => {
            return new AppKey(lineItem.appKey);
          });
      }),
      publishReplay(1),
      refCount(),
    );

    this.packages$ = this.packageIds$.pipe(
      switchMap((packageIds) => {
        return packageIds.length > 0 ? this.packageService.getMulti(packageIds) : of([]);
      }),
      publishReplay(1),
      refCount(),
    );

    this.includedAppKeys$ = zip(this.packages$, this.standaloneAppKeys$).pipe(
      map(([packages, standaloneAppKeys]) => {
        const packageAppKeys = packages
          .map((pkg) => {
            const lineItems = pkg && pkg.lineItems && pkg.lineItems.lineItems ? pkg.lineItems.lineItems : [];
            return lineItems.map(
              (item) =>
                new AppKey({
                  appId: item.id || '',
                  editionId: item.editionId || '',
                }),
            );
          })
          .reduce((a, b) => [...a, ...b], []);
        return standaloneAppKeys.concat(packageAppKeys);
      }),
      publishReplay(1),
      refCount(),
    );

    this.apps$ = combineLatest([this.includedAppKeys$, this.business$]).pipe(
      switchMap(([includedAppKeys, business]) =>
        this.appsService.getMulti(
          includedAppKeys,
          business.externalIdentifiers.partnerId,
          business.externalIdentifiers.marketId,
          null,
          true,
        ),
      ),
      publishReplay(1),
      refCount(),
    );

    this.uniqueApps$ = this.apps$.pipe(
      filter((apps) => !!apps && apps.length > 0),
      map((appKeys) => {
        return appKeys.reduce((unique, app) => {
          const { key } = app;
          if (
            !key ||
            !!unique.find(
              ({ key: uniqueKey }) => uniqueKey.appId === key.appId && uniqueKey.editionId === key.editionId,
            )
          ) {
            return unique;
          }

          unique.push(app);
          return unique;
        }, []);
      }),
      publishReplay(1),
      refCount(),
    );
  }

  initialize(businessId: string, orderId: string, lineItems: LineItemInterface[]): void {
    this.businessId$$.next(businessId || '');
    this.orderId$$.next(orderId || '');
    this.specifiedLineItems$$.next(lineItems || []);
  }
}
